import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Play,
  Settings,
  Bot,
  Package,
  Image,
  FileText,
  MessageSquare,
  BarChart3,
  Loader2,
  X,
  File,
  Video,
  Hash,
  Paperclip,
  ChevronDown,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Info,
  User,
  Music,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { listProducts, type Product } from "@/services/productsService";
import {
  executeSandboxTest,
  type TestResult as ApiTestResult,
} from "@/services/sandboxService";
import { FilePreviewModal } from "@/components/FilePreviewModal";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";

interface SandboxProps {
  onBack: () => void;
}

interface TestStep {
  stepNumber: number;
  stepName: string;
  prompt: string;
  input: string;
  output: string;
  status: "success" | "error" | "processing";
  duration: number;
  inputTokens: number;
  outputTokens: number;
  fileData?:
    | {
        name: string;
        type: string;
        size: number;
        url?: string;
      }[]
    | undefined;
}

interface TestResult {
  id: string;
  productId: string;
  input: string;
  output: string;
  timestamp: string;
  success: boolean;
  duration: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  steps: TestStep[];
  executedBy: string;
  fileData?:
    | {
        name: string;
        type: string;
        size: number;
        url?: string;
      }[]
    | undefined;
}

export const Sandbox = ({ onBack }: SandboxProps) => {
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [testInput, setTestInput] = useState<string>("");
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [expandedPrompts, setExpandedPrompts] = useState<Set<string>>(
    new Set()
  );
  const [expandedTests, setExpandedTests] = useState<Set<string>>(new Set());
  const [expandedFinalResults, setExpandedFinalResults] = useState<Set<string>>(
    new Set()
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(true);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { isAdminOrSupport } = usePermissions();

  // Filtrar especialistas: Ativo (1), Em desenvolvimento (3) ou Em teste (4)
  const availableProducts = products.filter(
    (p) => p.status_id === 1 || p.status_id === 3 || p.status_id === 4
  );
  const selectedProductData = availableProducts.find(
    (p) => p.id === selectedProduct
  );

  // Mapear tipos de agente para tipos de arquivos permitidos
  const getAgentFileRules = (agentTypeName?: string) => {
    if (!agentTypeName) return { allowed: "all", message: "Qualquer tipo de arquivo (máx. 10MB)" };

    const type = agentTypeName.toLowerCase();

    // Agentes que não permitem anexos
    if (["chat", "completions", "embeddings"].includes(type)) {
      return { allowed: "none", message: "Este agente não aceita anexos" };
    }

    // Agentes específicos por tipo
    if (type === "imagem") {
      return { allowed: "image", message: "Somente imagens (JPG, PNG, GIF, WebP - máx. 10MB)" };
    }

    if (type === "video" || type === "vídeo") {
      return { allowed: "video", message: "Somente vídeos (MP4, AVI, MOV, WMV - máx. 10MB)" };
    }

    if (type === "audio" || type === "áudio") {
      return { allowed: "audio", message: "Somente áudio (MP3, WAV, OGG, WebM - máx. 10MB)" };
    }

    if (["documento", "análise", "analise", "classificação", "classificacao"].includes(type)) {
      return { allowed: "document", message: "Somente documentos (PDF, TXT, JSON, XML, CSV, DOC, DOCX - máx. 10MB)" };
    }

    if (type === "custom") {
      return { allowed: "all", message: "Qualquer tipo de arquivo (máx. 10MB)" };
    }

    // Padrão: aceitar documentos
    return { allowed: "document", message: "Somente documentos (PDF, TXT, JSON, XML, CSV, DOC, DOCX - máx. 10MB)" };
  };

  // Obter accept do input baseado no tipo de agente
  const getFileAccept = (agentTypeName?: string) => {
    const rules = getAgentFileRules(agentTypeName);

    switch (rules.allowed) {
      case "none":
        return "";
      case "image":
        return "image/*";
      case "video":
        return "video/*";
      case "audio":
        return "audio/*";
      case "document":
        return ".pdf,.txt,.json,.xml,.csv,.doc,.docx";
      case "all":
      default:
        return "image/*,video/*,audio/*,.pdf,.txt,.json,.xml,.csv,.doc,.docx";
    }
  };

  const currentFileRules = getAgentFileRules(selectedProductData?.agent_type_name);
  const currentFileAccept = getFileAccept(selectedProductData?.agent_type_name);

  // Buscar especialistas da API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoadingProducts(true);
        const response = await listProducts();

        if (response.success && response.data) {
          setProducts(response.data.products);
        } else {
          toast({
            title: "Erro ao carregar especialistas",
            description:
              response.error?.message ||
              "Não foi possível carregar a lista de especialistas",
            variant: "destructive",
          });
        }
      } catch (error) {
        toast({
          title: "Erro ao carregar especialistas",
          description: "Ocorreu um erro ao buscar os especialistas",
          variant: "destructive",
        });
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [toast]);

  // Resetar anexos quando trocar de especialista
  useEffect(() => {
    if (selectedProduct) {
      setUploadedFiles([]);
      // Limpar o input de arquivo
      const fileInput = document.getElementById("file-upload") as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    }
  }, [selectedProduct]);

  const handleTest = async () => {
    if (!selectedProduct || (!testInput.trim() && uploadedFiles.length === 0)) {
      toast({
        title: "Erro",
        description:
          "Selecione um especialista e insira um input ou arquivo para teste",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Executar teste na API real
      const response = await executeSandboxTest({
        productId: selectedProduct,
        input: testInput,
        files: uploadedFiles,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || "Erro ao executar teste");
      }

      const apiResult = response.data;

      // Converter resultado da API para formato do frontend
      const newResult: TestResult = {
        id: apiResult.id,
        productId: apiResult.productId,
        input: apiResult.input,
        output: apiResult.output,
        timestamp: apiResult.timestamp,
        success: true,
        duration: apiResult.duration,
        inputTokens: apiResult.inputTokens,
        outputTokens: apiResult.outputTokens,
        totalTokens: apiResult.totalTokens,
        steps: apiResult.steps.map((step) => ({
          stepNumber: step.stepNumber,
          stepName: step.stepName,
          prompt: step.prompt,
          input: step.input,
          output: step.output || "",
          status: step.status as "success" | "error" | "processing",
          duration: step.duration,
          inputTokens: step.inputTokens,
          outputTokens: step.outputTokens,
          fileData:
            uploadedFiles.length > 0
              ? uploadedFiles.map((file) => ({
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  url: URL.createObjectURL(file),
                }))
              : undefined,
        })),
        executedBy: user?.name || apiResult.executedBy || "Usuário Atual",
        fileData:
          uploadedFiles.length > 0
            ? uploadedFiles.map((file) => ({
                name: file.name,
                type: file.type,
                size: file.size,
                url: URL.createObjectURL(file),
              }))
            : undefined,
      };

      setTestResults((prev) => [newResult, ...prev]);

      toast({
        title: "Teste Concluído",
        description: `Especialista testado com sucesso em ${apiResult.duration}ms`,
      });
    } catch (error: any) {
      const newResult: TestResult = {
        id: Date.now().toString(),
        productId: selectedProduct,
        input: testInput,
        output: error.message || "Erro no processamento",
        timestamp: new Date().toISOString(),
        success: false,
        duration: 0,
        inputTokens: 0,
        outputTokens: 0,
        totalTokens: 0,
        steps: [],
        executedBy: user?.name || "Usuário Atual",
        fileData:
          uploadedFiles.length > 0
            ? uploadedFiles.map((file) => ({
                name: file.name,
                type: file.type,
                size: file.size,
              }))
            : undefined,
      };

      setTestResults((prev) => [newResult, ...prev]);

      toast({
        title: "Erro no Teste",
        description: error.message || "Ocorreu um erro durante o processamento",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const simulateProductTest = async (
    product: any,
    input: string,
    files?: File[]
  ): Promise<string> => {
    // Simular delay de processamento
    await new Promise((resolve) =>
      setTimeout(resolve, 1000 + Math.random() * 2000)
    );

    // Adicionar informação sobre arquivos se presentes
    const fileInfo =
      files && files.length > 0
        ? `\n\n📎 Arquivo(s) anexado(s): ${files
            .map((f) => `${f.name} (${(f.size / 1024).toFixed(1)} KB)`)
            .join(", ")}`
        : "";

    // Simular diferentes tipos de resposta baseado no especialista
    switch (product.name) {
      case "Geração de Imagens IA":
        return `🎨 Imagem gerada com sucesso!\n\nPrompt: "${input}"${fileInfo}\n\nDescrição da imagem: Uma representação visual criativa baseada no prompt fornecido, utilizando técnicas de IA para geração de imagens.\n\nResolução: 1024x1024\nFormato: PNG\nTempo de processamento: ${
          Math.floor(Math.random() * 3) + 1
        }s`;

      case "Análise de Documentos":
        return `📄 Análise de Documento Concluída\n\nDocumento analisado: "${input}"${fileInfo}\n\nResumo:\n- Palavras-chave identificadas: 15\n- Sentimento: Positivo (85%)\n- Tópicos principais: Tecnologia, Inovação, Desenvolvimento\n- Confiança da análise: 92%\n\nRecomendações:\n- Documento bem estruturado\n- Linguagem técnica apropriada\n- Sugestão de revisão em 30 dias`;

      case "Processamento de Texto":
        return `📝 Texto Processado\n\nInput: "${input}"${fileInfo}\n\nResultado:\n- Palavras: ${
          input.split(" ").length
        }\n- Caracteres: ${input.length}\n- Sentimento: ${
          Math.random() > 0.5 ? "Positivo" : "Neutro"
        }\n- Idioma detectado: Português\n- Sugestões de melhoria: 3\n\nTexto otimizado:\n"${input.toUpperCase()}"`;

      case "Chatbot Inteligente":
        return `🤖 Resposta do Chatbot\n\nPergunta: "${input}"${fileInfo}\n\nResposta: Olá! Obrigado pela sua pergunta. Com base no que você perguntou sobre "${input}", posso ajudá-lo com informações relevantes e sugestões úteis. Como posso ser mais específico em minha resposta?\n\nConfiança: 87%\nTempo de resposta: 1.2s`;

      case "Análise de Sentimentos":
        return `😊 Análise de Sentimentos\n\nTexto analisado: "${input}"${fileInfo}\n\nResultados:\n- Sentimento geral: Positivo (78%)\n- Alegria: 65%\n- Confiança: 45%\n- Surpresa: 20%\n- Tristeza: 5%\n- Raiva: 2%\n\nPalavras-chave emocionais:\n- Positivas: bom, excelente, ótimo\n- Neutras: análise, dados, processo\n\nRecomendação: Continue com essa abordagem positiva!`;

      default:
        return `✅ Processamento Concluído\n\nInput: "${input}"${fileInfo}\n\nResultado: O especialista "${product.name}" processou com sucesso o input fornecido. Este é um resultado simulado para demonstração do sandbox.\n\nDetalhes técnicos:\n- Modelo: ${product.model_name}\n- Agente: ${product.agent_name}${product.agent_type_name ? ` (${product.agent_type_name})` : ''}\n- Status: Processado com sucesso`;
    }
  };

  const validateFile = (file: File): boolean => {
    const rules = getAgentFileRules(selectedProductData?.agent_type_name);

    // Verificar se o agente aceita anexos
    if (rules.allowed === "none") {
      toast({
        title: "Anexos não permitidos",
        description: "Este agente não aceita arquivos anexados",
        variant: "destructive",
      });
      return false;
    }

    // Definir tipos permitidos baseado nas regras do agente
    let allowedTypes: string[] = [];

    switch (rules.allowed) {
      case "image":
        allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
        break;
      case "video":
        allowedTypes = [
          "video/mp4",
          "video/avi",
          "video/mov",
          "video/wmv",
          "video/quicktime",
        ];
        break;
      case "audio":
        allowedTypes = [
          "audio/mpeg",
          "audio/mp3",
          "audio/wav",
          "audio/ogg",
          "audio/webm",
        ];
        break;
      case "document":
        allowedTypes = [
          "application/pdf",
          "text/plain",
          "application/json",
          "application/xml",
          "text/xml",
          "text/csv",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        break;
      case "all":
      default:
        allowedTypes = [
          // Imagens
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
          // Documentos
          "application/pdf",
          "text/plain",
          "application/json",
          "application/xml",
          "text/xml",
          "text/csv",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          // Vídeos
          "video/mp4",
          "video/avi",
          "video/mov",
          "video/wmv",
          "video/quicktime",
          // Áudio
          "audio/mpeg",
          "audio/mp3",
          "audio/wav",
          "audio/ogg",
          "audio/webm",
        ];
    }

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Tipo de arquivo não permitido",
        description: rules.message,
        variant: "destructive",
      });
      return false;
    }

    // Validar tamanho (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Arquivo muito grande",
        description: "O arquivo deve ter no máximo 10MB",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(validateFile);

    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
      toast({
        title: "Arquivo(s) carregado(s)",
        description: `${validFiles.length} arquivo(s) foi(ram) carregado(s) com sucesso`,
      });
    }

    // Limpar o input para permitir upload do mesmo arquivo novamente
    event.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    // Verificar se o agente aceita anexos
    if (currentFileRules.allowed === "none") {
      toast({
        title: "Anexos não permitidos",
        description: "Este agente não aceita arquivos anexados",
        variant: "destructive",
      });
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(validateFile);

    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
      toast({
        title: "Arquivo(s) carregado(s)",
        description: `${validFiles.length} arquivo(s) foi(ram) carregado(s) com sucesso`,
      });
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) return <Image className="h-4 w-4" />;
    if (fileType.startsWith("video/")) return <Video className="h-4 w-4" />;
    if (fileType.startsWith("audio/")) return <Music className="h-4 w-4" />;
    return <File className="h-4 w-4" />;
  };

  const handleFileClick = (file: File) => {
    setPreviewFile(file);
    setIsPreviewOpen(true);
  };

  const renderFileThumbnail = (file: File) => {
    if (file.type.startsWith("image/")) {
      return (
        <img
          src={URL.createObjectURL(file)}
          alt={file.name}
          className="w-12 h-12 object-cover rounded"
        />
      );
    }

    if (file.type.startsWith("video/")) {
      return (
        <div className="w-12 h-12 bg-purple-100 rounded flex items-center justify-center">
          <Video className="h-6 w-6 text-purple-600" />
        </div>
      );
    }

    if (file.type.startsWith("audio/")) {
      return (
        <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
          <Music className="h-6 w-6 text-blue-600" />
        </div>
      );
    }

    if (file.type === "application/pdf") {
      return (
        <div className="w-12 h-12 bg-red-100 rounded flex items-center justify-center">
          <FileText className="h-6 w-6 text-red-600" />
        </div>
      );
    }

    return (
      <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
        <File className="h-6 w-6 text-gray-600" />
      </div>
    );
  };

  const generateTestSteps = (
    product: any,
    input: string,
    files: File[],
    finalOutput: string
  ): TestStep[] => {
    const steps: TestStep[] = [];

    // Simular diferentes números de etapas baseado no especialista
    const stepCount = Math.floor(Math.random() * 3) + 1; // 1-3 etapas

    for (let i = 0; i < stepCount; i++) {
      const stepNumber = i + 1;
      const stepNames = [
        "Análise Inicial",
        "Processamento de Dados",
        "Geração de Conteúdo",
        "Validação de Resultado",
        "Finalização",
      ];

      const stepName = stepNames[i] || `Etapa ${stepNumber}`;
      const prompt = `Prompt para ${stepName}: Processar "${input}" usando ${product.name}`;

      // Simular output parcial para cada etapa
      const stepOutput =
        i === stepCount - 1
          ? finalOutput
          : `Resultado parcial da ${stepName}: Processamento em andamento...`;

      steps.push({
        stepNumber,
        stepName,
        prompt,
        input: i === 0 ? input : `Resultado da etapa anterior`,
        output: stepOutput,
        status: "success" as const,
        duration: Math.floor(Math.random() * 1000) + 500,
        inputTokens: Math.floor(input.length / 4) + files.length * 50,
        outputTokens: Math.floor(stepOutput.length / 4),
        fileData:
          i === 0 && files.length > 0
            ? files.map((file) => ({
                name: file.name,
                type: file.type,
                size: file.size,
                url: URL.createObjectURL(file),
              }))
            : undefined,
      });
    }

    return steps;
  };

  const toggleStepExpansion = (resultId: string, stepNumber: number) => {
    const key = `${resultId}-${stepNumber}`;
    setExpandedSteps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const togglePromptExpansion = (resultId: string, stepNumber: number) => {
    const key = `${resultId}-${stepNumber}-prompt`;
    setExpandedPrompts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case "processing":
        return <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />;
      default:
        return <Info className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatDateTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString("pt-BR"),
      time: date.toLocaleTimeString("pt-BR"),
    };
  };

  const toggleTestExpansion = (resultId: string) => {
    setExpandedTests((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  const toggleFinalResultExpansion = (resultId: string) => {
    setExpandedFinalResults((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  // Auto-expandir o teste mais recente
  useEffect(() => {
    if (testResults.length > 0 && testResults[0]) {
      const latestResultId = testResults[0].id;
      setExpandedTests((prev) => {
        const newSet = new Set(prev);
        newSet.add(latestResultId);
        return newSet;
      });
    }
  }, [testResults.length]);

  const getProductIcon = (productName: string) => {
    switch (productName) {
      case "Geração de Imagens IA":
        return <Image className="h-4 w-4" />;
      case "Análise de Documentos":
        return <FileText className="h-4 w-4" />;
      case "Processamento de Texto":
        return <FileText className="h-4 w-4" />;
      case "Chatbot Inteligente":
        return <MessageSquare className="h-4 w-4" />;
      case "Análise de Sentimentos":
        return <BarChart3 className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const getInputPlaceholder = (productName: string) => {
    switch (productName) {
      case "Geração de Imagens IA":
        return "Descreva a imagem que deseja gerar...";
      case "Análise de Documentos":
        return "Cole o texto do documento para análise...";
      case "Processamento de Texto":
        return "Digite o texto para processamento...";
      case "Chatbot Inteligente":
        return "Digite sua pergunta ou mensagem...";
      case "Análise de Sentimentos":
        return "Digite o texto para análise de sentimentos...";
      default:
        return "Digite o input para teste...";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Settings}
        title="Sandbox de Testes"
        description="Teste especialistas de IA em ambiente seguro"
        onBack={onBack}
      />

      {/* Main Content */}
      <main className="container mx-auto p-6 min-h-[calc(100vh-4rem)] flex flex-col">
        <div className={`grid gap-6 flex-1 min-h-0 ${isAdminOrSupport ? 'lg:grid-cols-2' : 'lg:grid-cols-1'}`}>
          {/* Painel de Configuração */}
          <Card className="flex flex-col h-[70vh]">
            <CardHeader className="flex-shrink-0 mx-6 mt-6 mb-2 px-6 pt-6 pb-2">
              <CardTitle className="flex items-center">
                <Settings className="h-5 w-5 mr-2" />
                Configuração do Teste
              </CardTitle>
              <CardDescription>
                Selecione um especialista e configure o teste
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col min-h-0 px-6 pt-2 pb-2">
              <div className="space-y-3 flex-1 p-3">
                <div className="p-3">
                  <div className="flex items-center space-x-3">
                    <Label
                      htmlFor="product"
                      className="text-sm font-medium whitespace-nowrap"
                    >
                      Especialista:
                    </Label>
                    <Select
                      value={selectedProduct}
                      onValueChange={setSelectedProduct}
                      disabled={isLoadingProducts}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue
                          placeholder={
                            isLoadingProducts
                              ? "Carregando especialistas..."
                              : "Selecione um especialista"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {availableProducts.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            <div className="flex items-center">
                              {getProductIcon(product.name)}
                              <span className="ml-2">{product.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {selectedProductData && (
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {getProductIcon(selectedProductData.name)}
                        <h4 className="font-medium text-sm">
                          {selectedProductData.name}
                        </h4>
                      </div>
                      <div className="flex items-center space-x-3 text-xs">
                        <div className="flex items-center">
                          <Bot className="h-3 w-3 mr-1" />
                          {selectedProductData.model_name}
                        </div>
                        <div className="flex items-center">
                          <Settings className="h-3 w-3 mr-1" />
                          {selectedProductData.agent_name}
                          {selectedProductData.agent_type_name && (
                            <span className="text-muted-foreground ml-1">
                              ({selectedProductData.agent_type_name})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {selectedProductData.description}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="input">Input para Teste</Label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="file"
                        id="file-upload"
                        onChange={handleFileUpload}
                        accept={currentFileAccept}
                        className="hidden"
                        multiple
                        disabled={currentFileRules.allowed === "none"}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          document.getElementById("file-upload")?.click()
                        }
                        disabled={currentFileRules.allowed === "none"}
                      >
                        <Paperclip className="h-4 w-4 mr-1" />
                        Anexar
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    id="input"
                    value={testInput}
                    onChange={(e) => setTestInput(e.target.value)}
                    onDragOver={currentFileRules.allowed !== "none" ? handleDragOver : undefined}
                    onDragLeave={currentFileRules.allowed !== "none" ? handleDragLeave : undefined}
                    onDrop={currentFileRules.allowed !== "none" ? handleDrop : undefined}
                    placeholder={
                      selectedProductData
                        ? getInputPlaceholder(selectedProductData.name)
                        : "Digite o input para teste ou arraste arquivos aqui..."
                    }
                    rows={4}
                    className={`resize-none transition-colors ${
                      isDragOver && currentFileRules.allowed !== "none"
                        ? "border-primary bg-primary/5"
                        : ""
                    }`}
                  />
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Label className="text-xs font-medium text-muted-foreground">
                          Arquivos Anexados:
                        </Label>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                          {uploadedFiles.length}
                        </span>
                      </div>
                      {selectedProductData && (
                        <span className={`text-xs ${
                          currentFileRules.allowed === "none"
                            ? "text-destructive"
                            : "text-muted-foreground"
                        }`}>
                          {currentFileRules.message}
                        </span>
                      )}
                    </div>
                    <div className="border-2 border-primary/20 rounded-lg p-3 bg-primary/5 h-24 overflow-y-auto">
                      <div className="space-y-2 pr-2">
                        {uploadedFiles.length > 0 ? (
                          uploadedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 bg-background rounded-lg border border-border/50 hover:bg-accent/50 transition-colors cursor-pointer group"
                              onClick={() => handleFileClick(file)}
                            >
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                {renderFileThumbnail(file)}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {file.name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {(file.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeFile(index);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))
                        ) : (
                          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                            Nenhum arquivo anexado
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-shrink-0 mt-2 pt-3 border-t p-3">
                <Button
                  onClick={handleTest}
                  disabled={
                    !selectedProduct ||
                    (!testInput.trim() && uploadedFiles.length === 0) ||
                    isLoading
                  }
                  className="w-full bg-gradient-primary text-white hover:opacity-90"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Executar Teste
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Painel de Resultados - Apenas para Administrador e Suporte */}
          {isAdminOrSupport && (
            <Card className="flex flex-col h-[70vh]">
              <CardHeader className="mx-6 mt-6 mb-2 px-6 pt-6 pb-2">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Package className="h-5 w-5 mr-2" />
                    Resultados dos Testes
                  </div>
                  <Badge variant="secondary">
                    {testResults.length} teste
                    {testResults.length !== 1 ? "s" : ""}
                  </Badge>
                </CardTitle>
                <CardDescription>Histórico de testes executados</CardDescription>
              </CardHeader>
              <CardContent className="px-6 pt-2 pb-6 flex-1 flex flex-col min-h-0">
                {testResults.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground px-6 flex-1 flex items-center justify-center">
                    <div>
                      <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhum teste executado ainda</p>
                      <p className="text-sm">
                        Execute um teste para ver os resultados aqui
                      </p>
                    </div>
                  </div>
                ) : (
                <div className="flex-1 overflow-hidden">
                  <div className="h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 border border-muted/20 rounded-lg">
                    {testResults.map((result, index) => {
                      const product = availableProducts.find(
                        (p) => p.id === result.productId
                      );
                      const { date, time } = formatDateTime(result.timestamp);
                      const isTestExpanded = expandedTests.has(result.id);
                      const isFinalResultExpanded = expandedFinalResults.has(
                        result.id
                      );
                      const isLatestTest = index === 0;

                      return (
                        <div
                          key={result.id}
                          className={`border-b last:border-b-0 ${
                            result.success ? "bg-green-50/50" : "bg-red-50/50"
                          }`}
                        >
                          {/* Header do Resultado - Sempre Visível */}
                          <div
                            className="p-4 cursor-pointer hover:bg-muted/40 transition-colors"
                            onClick={() => toggleTestExpansion(result.id)}
                          >
                            {/* Primeira linha: Título e Status */}
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                {isTestExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                                {product && getProductIcon(product.name)}
                                <span className="font-medium text-sm">
                                  {product?.name || "Especialista Desconhecido"}
                                </span>
                                <Badge
                                  variant={
                                    result.success ? "default" : "destructive"
                                  }
                                  className="text-xs"
                                >
                                  {result.success ? "Sucesso" : "Erro"}
                                </Badge>
                                {isLatestTest && (
                                  <Badge variant="outline" className="text-xs">
                                    Mais Recente
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                <span>
                                  {date} {time}
                                </span>
                              </div>
                            </div>

                            {/* Segunda linha: Métricas e Usuário */}
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-1">
                                  <Hash className="h-3 w-3" />
                                  <span className="text-blue-600">
                                    Entrada: {result.inputTokens}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Hash className="h-3 w-3" />
                                  <span className="text-green-600">
                                    Saída: {result.outputTokens}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Hash className="h-3 w-3" />
                                  <span className="text-purple-600">
                                    Total: {result.totalTokens}
                                  </span>
                                </div>
                                <div className="text-muted-foreground">
                                  Duração: {result.duration}ms
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                <User className="h-3 w-3" />
                                <span className="text-red-600 font-medium">
                                  {result.executedBy}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Conteúdo do Teste (Collapsible) */}
                          {isTestExpanded && (
                            <div className="border-t">
                              {/* Etapas do Processo */}
                              <div className="p-4 space-y-3">
                                {result.steps.map((step) => {
                                  const stepKey = `${result.id}-${step.stepNumber}`;
                                  const isStepExpanded =
                                    expandedSteps.has(stepKey);
                                  const promptKey = `${result.id}-${step.stepNumber}-prompt`;
                                  const isPromptExpanded =
                                    expandedPrompts.has(promptKey);

                                  return (
                                    <div
                                      key={step.stepNumber}
                                      className="border rounded-lg"
                                    >
                                      {/* Header da Etapa */}
                                      <div
                                        className="p-3 bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors"
                                        onClick={() =>
                                          toggleStepExpansion(
                                            result.id,
                                            step.stepNumber
                                          )
                                        }
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center space-x-2">
                                            {isStepExpanded ? (
                                              <ChevronDown className="h-4 w-4" />
                                            ) : (
                                              <ChevronRight className="h-4 w-4" />
                                            )}
                                            <span className="font-medium text-sm">
                                              Etapa {step.stepNumber} -{" "}
                                              {step.stepName}
                                            </span>
                                            {getStatusIcon(step.status)}
                                          </div>
                                          <div className="text-xs text-muted-foreground">
                                            {step.duration}ms
                                          </div>
                                        </div>
                                      </div>

                                      {/* Conteúdo da Etapa (Collapsible) */}
                                      {isStepExpanded && (
                                        <div className="p-3 space-y-3 border-t">
                                          {/* Prompt do Agente */}
                                          <div>
                                            <div
                                              className="flex items-center justify-between cursor-pointer hover:bg-muted/30 p-2 rounded"
                                              onClick={() =>
                                                togglePromptExpansion(
                                                  result.id,
                                                  step.stepNumber
                                                )
                                              }
                                            >
                                              <span className="text-xs font-medium text-muted-foreground">
                                                Prompt Agent
                                              </span>
                                              {isPromptExpanded ? (
                                                <ChevronDown className="h-3 w-3" />
                                              ) : (
                                                <ChevronRight className="h-3 w-3" />
                                              )}
                                            </div>
                                            {isPromptExpanded && (
                                              <div className="text-xs bg-muted p-2 rounded mt-1 font-mono">
                                                {step.prompt}
                                              </div>
                                            )}
                                          </div>

                                          {/* Input */}
                                          <div>
                                            <Label className="text-xs font-medium text-muted-foreground">
                                              Input:
                                            </Label>
                                            <div className="text-sm bg-white p-2 rounded border mt-1">
                                              {step.input}
                                            </div>
                                            {step.fileData && step.fileData.length > 0 && (
                                              <div className="space-y-1 mt-1">
                                                {step.fileData.map((file, fileIndex) => (
                                                  <div key={fileIndex} className="flex items-center space-x-2 text-xs bg-muted p-2 rounded">
                                                    {getFileIcon(file.type)}
                                                    <span className="font-medium">
                                                      {file.name}
                                                    </span>
                                                    <span className="text-muted-foreground">
                                                      ({(file.size / 1024).toFixed(1)} KB)
                                                    </span>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>

                                          {/* Output */}
                                          <div>
                                            <Label className="text-xs font-medium text-muted-foreground">
                                              Output:
                                            </Label>
                                            <div className="text-sm bg-white p-2 rounded border mt-1 whitespace-pre-wrap">
                                              {step.output}
                                            </div>
                                          </div>

                                          {/* Status da Etapa */}
                                          <div className="flex items-center space-x-2 text-xs">
                                            {getStatusIcon(step.status)}
                                            <span className="capitalize">
                                              {step.status}
                                            </span>
                                            <span className="text-muted-foreground">
                                              •{" "}
                                              {step.inputTokens +
                                                step.outputTokens}{" "}
                                              tokens
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}

                                {/* Resultado Final Consolidado */}
                                <div className="border-t bg-muted/30">
                                  <div
                                    className="p-4 cursor-pointer hover:bg-muted/40 transition-colors"
                                    onClick={() =>
                                      toggleFinalResultExpansion(result.id)
                                    }
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-2">
                                        {isFinalResultExpanded ? (
                                          <ChevronDown className="h-4 w-4" />
                                        ) : (
                                          <ChevronRight className="h-4 w-4" />
                                        )}
                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                        <span className="font-medium text-sm">
                                          Resultado Final
                                        </span>
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {result.totalTokens} tokens •{" "}
                                        {result.duration}ms
                                      </div>
                                    </div>
                                  </div>

                                  {isFinalResultExpanded && (
                                    <div className="p-4 border-t">
                                      <div className="text-sm bg-white p-2 rounded border whitespace-pre-wrap">
                                        {result.output}
                                      </div>
                                      <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
                                        <span>
                                          Total: {result.totalTokens} tokens
                                        </span>
                                        <span>
                                          Duração: {result.duration}ms
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Estatísticas - Apenas para Administrador e Suporte */}
        {isAdminOrSupport && (
          <Card className="mt-6 flex-shrink-0">
            <CardHeader>
              <CardTitle>Estatísticas dos Testes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {testResults.filter((r) => r.success).length}
                  </div>
                  <p className="text-sm text-muted-foreground">Sucessos</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {testResults.filter((r) => !r.success).length}
                  </div>
                  <p className="text-sm text-muted-foreground">Erros</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {testResults.length > 0
                      ? Math.round(
                          testResults.reduce((acc, r) => acc + r.duration, 0) /
                            testResults.length
                        )
                      : 0}
                    ms
                  </div>
                  <p className="text-sm text-muted-foreground">Tempo Médio</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {testResults.length > 0
                      ? Math.round(
                          (testResults.filter((r) => r.success).length /
                            testResults.length) *
                            100
                        )
                      : 0}
                    %
                  </div>
                  <p className="text-sm text-muted-foreground">Taxa de Sucesso</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Modal de Preview de Arquivos */}
      <FilePreviewModal
        file={previewFile}
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewFile(null);
        }}
      />
    </div>
  );
};
